/**
 * Notus / site-specific JavaScript.
 * Theme core files stay untouched; behaviour is extended here.
 * Loaded after theme-script (FoxThemeSettings, routes, etc. are available).
 *
 * PDP gift / personalisation
 * -------------------------
 * `notus-product-personalisation.liquid` outputs `#virtual-form`, `#personalised-form`, and a
 * JSON blob (`[data-notus-variant-flags]`) with per-variant booleans from metafields
 * `custom.variant_is_virtual` and `custom.variant_is_personalised`. The script keys off the
 * **currently selected variant id** (main form `input[name="id"]` or sticky `select[name="id"]`),
 * so it works for single- and multi-option products without duplicate radios.
 *
 * `initNotusPdpEmptyBlocks` hides empty promo `custom_liquid` blocks and the whole
 * `variant_picker` section when there are no option fields and no personalisation variants.
 *
 * Predictive search + product ref (variant SKU)
 * --------------------------------------------
 * Patches `<predictive-search>` at runtime (see `initNotusPredictiveSearchSku`) so `search-drawer.js`
 * stays stock; SKU index from `templates/page.sku-json.liquid` + page handle in `pageUrls`.
 *
 * Offers Sidebar
 * -------------
 * Wires up the #ob-trigger button (hardcoded in header.liquid design-6) to open the offers
 * sidebar drawer rendered by the App Embed block. Close/keyboard handled in offers-sidebar.js.
 *
 * Price Filter Debounce
 * --------------------
 * Prevents the price range filter inputs from firing on every keystroke.
 * Waits 600ms after the user stops typing before applying the filter.
 */
(function () {
  'use strict';

  /**
   * Header inline search (`snippets/header__search.liquid`): readonly field opens
   * `#Drawer-Search` and focuses the drawer input via the drawer component API (`global.js`).
   */
  function initHeaderInlineSearchDrawer() {
    var fields = document.querySelectorAll('.header-search-form .f-search-no-js__field');
    var drawer = document.getElementById('Drawer-Search');

    if (!fields.length || !drawer || typeof drawer.openDrawer !== 'function') return;

    fields.forEach(function (field) {
      var openerEl =
        field.querySelector('input.f-search-no-js__input[readonly]') ||
        field.querySelector('input.f-search-no-js__input');
      var form = field.closest('form');
      if (!openerEl) return;

      function openSearchDrawer(e) {
        e.preventDefault();
        drawer.openDrawer(openerEl);
      }

      field.addEventListener('click', openSearchDrawer);
      if (form) {
        form.addEventListener('submit', openSearchDrawer);
      }
    });
  }

  /**
   * Stops the browser from submitting and blocks other listeners on the same element
   * (capture-phase handlers use this when validation fails).
   */
  function stopSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }
  }

  /**
   * Gift panels + line-item properties for virtual / personalised variants.
   * Flags come from Liquid JSON; variant id from the form being used (main PDP or sticky ATC).
   */
  function initProductPersonalisation() {
    var mainProductForm = document.querySelector('.main-product-form');
    var personalisationRoot = document.getElementById('personalisation-form');
    var flagsEl = personalisationRoot
      ? personalisationRoot.querySelector('[data-notus-variant-flags]')
      : null;
    var virtualForm = document.getElementById('virtual-form');
    var personalisedForm = document.getElementById('personalised-form');

    /** Sticky bar uses `form.sticky-atc-form` + dynamic id `product-form-{section.id}` — not a fixed id. */
    var stickyAtcForm = document.querySelector('form.sticky-atc-form');
    var stickyVariantSelect = stickyAtcForm ? stickyAtcForm.querySelector('select[name="id"]') : null;

    if (!mainProductForm || !flagsEl || (!virtualForm && !personalisedForm)) {
      return;
    }

    var variantFlagsById = {};
    try {
      variantFlagsById = JSON.parse(flagsEl.textContent.trim());
    } catch (e) {
      console.error('[Notus personalisation] Invalid variant flags JSON', e);
      return;
    }

    var variantIdInput = mainProductForm.querySelector('input[name="id"]');
    if (!variantIdInput) {
      return;
    }

    function getVariantIdForMainForm() {
      return String(variantIdInput.value || '').trim();
    }

    function getFlagsForVariant(variantId) {
      var f = variantFlagsById[variantId];
      if (!f || typeof f !== 'object') {
        return { isVirtual: false, isPersonalised: false };
      }
      return {
        isVirtual: f.isVirtual === true,
        isPersonalised: f.isPersonalised === true,
      };
    }

    /**
     * @param {string} [variantIdOverride] — e.g. sticky `select` value before main `input[name=id]` syncs.
     */
    function updateFormVisibility(variantIdOverride) {
      var vid =
        variantIdOverride !== undefined && variantIdOverride !== null
          ? String(variantIdOverride).trim()
          : getVariantIdForMainForm();
      var flags = getFlagsForVariant(vid);

      if (flags.isVirtual) {
        if (virtualForm) virtualForm.classList.add('form-visible');
        if (personalisedForm) personalisedForm.classList.remove('form-visible');
      } else if (flags.isPersonalised) {
        if (personalisedForm) personalisedForm.classList.add('form-visible');
        if (virtualForm) virtualForm.classList.remove('form-visible');
      } else {
        if (virtualForm) virtualForm.classList.remove('form-visible');
        if (personalisedForm) personalisedForm.classList.remove('form-visible');
      }
    }

    function isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function clearFieldBorderErrors() {
      [virtualForm, personalisedForm].forEach(function (form) {
        if (!form) return;
        form.querySelectorAll('input, textarea').forEach(function (field) {
          field.style.border = '';
        });
      });
    }

    /**
     * @param {'main'|'sticky'} source — which control owns the variant id for this submit.
     */
    function validateForm(source) {
      var variantId =
        source === 'sticky' && stickyVariantSelect
          ? String(stickyVariantSelect.value || '').trim()
          : getVariantIdForMainForm();
      var flags = getFlagsForVariant(variantId);

      if (!flags.isVirtual && !flags.isPersonalised) return true;

      var isValid = true;
      clearFieldBorderErrors();

      if (flags.isVirtual && virtualForm) {
        virtualForm.querySelectorAll('input, textarea').forEach(function (field) {
          if (!field.value) {
            field.style.border = '1px solid red';
            isValid = false;
          }
        });

        var recipientEmailEl = document.getElementById('recipient_email');
        var confirmRecipientEmailEl = document.getElementById('confirm_recipient_email');
        var recipientEmail = recipientEmailEl ? recipientEmailEl.value : '';
        var confirmRecipientEmail = confirmRecipientEmailEl ? confirmRecipientEmailEl.value : '';

        if (!isValidEmail(recipientEmail)) {
          window.alert('Please enter a valid email address for the recipient.');
          isValid = false;
        }

        if (recipientEmail !== confirmRecipientEmail) {
          window.alert('Recipient Email and Confirm Recipient Email must match.');
          isValid = false;
        }
      }

      if (flags.isPersonalised && personalisedForm) {
        personalisedForm.querySelectorAll('input, textarea').forEach(function (field) {
          if (!field.value) {
            field.style.border = '1px solid red';
            isValid = false;
          }
        });
      }

      if (!isValid) {
        window.alert('Please complete all required fields before adding to the cart.');
      }

      return isValid;
    }

    /**
     * @param {'main'|'sticky'} source
     */
    function addPersonalisationFieldsToForm(targetForm, source) {
      if (!targetForm) return;

      targetForm.querySelectorAll('.personalisation-field').forEach(function (field) {
        field.remove();
      });

      var variantId =
        source === 'sticky' && stickyVariantSelect
          ? String(stickyVariantSelect.value || '').trim()
          : getVariantIdForMainForm();
      var flags = getFlagsForVariant(variantId);

      var activeForm = null;
      if (flags.isVirtual && virtualForm) {
        activeForm = virtualForm;
      } else if (flags.isPersonalised && personalisedForm) {
        activeForm = personalisedForm;
      }

      if (!activeForm) return;

      var inputs = activeForm.querySelectorAll('input, textarea');
      var dataObject = {};

      inputs.forEach(function (input) {
        var value = (input.value || '').trim();
        if (input.name && value) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'properties[' + input.name + ']';
          hidden.value = value;
          hidden.classList.add('personalisation-field');
          targetForm.appendChild(hidden);

          dataObject[input.name] = value;
        }
      });

      var combinedMessage = Object.keys(dataObject)
        .map(function (key) {
          return key + ': ' + dataObject[key];
        })
        .join(', ');

      if (combinedMessage) {
        var combinedInput = document.createElement('input');
        combinedInput.type = 'hidden';
        combinedInput.name = 'properties[Message]';
        combinedInput.value = combinedMessage;
        combinedInput.classList.add('personalisation-field');
        targetForm.appendChild(combinedInput);
      }
    }

    var personalisationErrorMsg =
      'There was a problem with the personalisation fields. Please check the details and try again.';

    variantIdInput.addEventListener('change', function () {
      updateFormVisibility();
    });

    document.addEventListener('variant:changed', function () {
      updateFormVisibility();
    });

    if (stickyVariantSelect) {
      stickyVariantSelect.addEventListener('change', function () {
        updateFormVisibility(stickyVariantSelect.value);
      });
    }

    mainProductForm.addEventListener(
      'submit',
      function (event) {
        try {
          if (!validateForm('main')) {
            stopSubmit(event);
            return false;
          }
          addPersonalisationFieldsToForm(mainProductForm, 'main');
        } catch (e) {
          console.error('[Notus personalisation] Main form submit', e);
          stopSubmit(event);
          window.alert(personalisationErrorMsg);
          return false;
        }
      },
      true
    );

    if (stickyAtcForm && stickyVariantSelect) {
      stickyAtcForm.addEventListener(
        'submit',
        function (event) {
          try {
            var variantId = String(stickyVariantSelect.value || '').trim();
            var flags = getFlagsForVariant(variantId);

            if (!flags.isVirtual && !flags.isPersonalised) {
              return;
            }

            if (!validateForm('sticky')) {
              stopSubmit(event);
              return false;
            }

            addPersonalisationFieldsToForm(stickyAtcForm, 'sticky');
          } catch (e) {
            console.error('[Notus personalisation] Sticky ATC submit', e);
            stopSubmit(event);
            window.alert(personalisationErrorMsg);
            return false;
          }
        },
        true
      );
    }

    updateFormVisibility();
  }

  /**
   * Gift Aid checkbox (cart drawer + `/cart`) — saves cart attribute via Ajax without editing theme `cart.js` / `cart-drawer.js`.
   * Markup: `snippets/notus-cart-gift-aid.liquid` (`.gift-aid-section`).
   */
  function initNotusCartGiftAid() {
    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t || t.type !== 'checkbox' || !t.closest || !t.closest('.gift-aid-section')) {
          return;
        }
        if (!t.name || String(t.name).indexOf('Gift Aid') === -1) {
          return;
        }
        var checked = !!t.checked;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Gift Aid': checked ? 'Yes' : 'No',
            },
          }),
        }).catch(function (err) {
          console.error('[Notus] Gift Aid update failed', err);
        });
      },
      false
    );
  }

  /**
   * Patches the theme's `<predictive-search>` custom element (from `search-drawer.js`, stock) so we
   * don't fork that file. Merges "product ref" / variant SKU hits from `templates/page.sku-json.liquid`
   * (paginate JSON) — same URLs as pre-update: `/pages/sku-json-kasjfhgc?…` (extend `?page=` if catalog
   * grows past 4×250). Cache: `window._skuDataset`
   */
  function initNotusPredictiveSearchSku() {
    function applyPatch(Constructor) {
      if (!Constructor || !Constructor.prototype || Constructor.prototype._notusSkuPatched) {
        return;
      }
      var orig = Constructor.prototype.renderSearchResults;
      Constructor.prototype.renderSearchResults = function (resultsMarkup) {
        orig.call(this, resultsMarkup);
        notusInjectSkuMatchesIntoPredictive(this, this.searchTerm);
      };
      Constructor.prototype._notusSkuPatched = true;
    }

    if (customElements.get('predictive-search')) {
      applyPatch(customElements.get('predictive-search'));
    } else {
      customElements.whenDefined('predictive-search').then(function (Constructor) {
        applyPatch(Constructor);
      });
    }
  }

  /**
   * @param {HTMLElement} el — the predictive-search instance
   * @param {string} searchTerm
   */
  function notusInjectSkuMatchesIntoPredictive(el, searchTerm) {
    var pageUrls = [
      '/pages/sku-json-kasjfhgc',
      '/pages/sku-json-kasjfhgc?page=2',
      '/pages/sku-json-kasjfhgc?page=3',
      '/pages/sku-json-kasjfhgc?page=4',
    ];
    var container = el.predictiveSearchResults;
    if (!container) {
      return;
    }

    function removeGroup() {
      var existing = container.querySelector('#sku-match-group');
      if (existing) {
        existing.remove();
      }
    }

    if (!searchTerm || String(searchTerm).length < 2) {
      removeGroup();
      return;
    }

    if (!window._skuDataset) {
      window._skuDataset = [];
    }

    (async function () {
      if (!window._skuDataset.length) {
        try {
          var results = await Promise.all(
            pageUrls.map(async function (url) {
              var res = await fetch(url);
              if (!res.ok) {
                throw new Error('Fetch failed for ' + url);
              }
              var json = await res.json();
              var products = Array.isArray(json.products) ? json.products : [];
              return products.flatMap(function (product) {
                var variants = Array.isArray(product.variants) ? product.variants : [];
                return variants.map(function (variant) {
                  return {
                    title: product.title,
                    url: product.url,
                    image: product.image,
                    sku: variant.sku,
                  };
                });
              });
            })
          );
          window._skuDataset = results.flat();
        } catch (err) {
          console.error('[Notus] Failed to load SKU / product ref index', err);
          return;
        }
      }

      var q = String(searchTerm).toLowerCase();
      var matches = window._skuDataset.filter(function (p) {
        return p.sku && String(p.sku).toLowerCase().indexOf(q) !== -1;
      });

      if (!matches.length) {
        removeGroup();
        return;
      }

      function imageBlock(image, title) {
        if (!image) {
          return '<div class="f-predictive-search__item-image f-image" style="--aspect-ratio:1"></div>';
        }
        return (
          '<div class="f-predictive-search__item-image f-image" style="--aspect-ratio:1">' +
          '<img src="' +
          image +
          '" alt="' +
          String(title).replace(/"/g, '&quot;') +
          '" width="70" height="70" loading="lazy">' +
          '</div>'
        );
      }

      var html =
        '\n        <div class="f-predictive-search__result-group f-predictive-search__result-products" id="sku-match-group">\n' +
        '          <h2 class="f-predictive-search__heading font-body-bolder">Product ref matches</h2>\n' +
        '          <ul class="f-predictive-search__results-list list-none" role="listbox">\n' +
        matches
          .map(function (p, i) {
            return (
              '              <li id="f-predictive-search-option-sku-' +
              i +
              '" role="option" aria-selected="false">\n' +
              '                <a href="' +
              p.url +
              '" class="f-predictive-search__item f-image-hover-effect__none" tabindex="-1">\n' +
              '                  ' +
              imageBlock(p.image, p.title) +
              '\n' +
              '                  <div class="f-predictive-search__item-content">\n' +
              '                    <h3 class="f-predictive-search__item-heading text-product-title">' +
              p.title +
              '</h3>\n' +
              '                    <div class="f-predictive-search__item-vendor text-subtext">Product ref: ' +
              p.sku +
              '</div>\n' +
              '                  </div>\n' +
              '                </a>\n' +
              '              </li>\n'
            );
          })
          .join('') +
        '          </ul>\n' +
        '        </div>\n      ';

      removeGroup();
      container.insertAdjacentHTML('afterbegin', html);
    })();
  }

  /**
   * Account popover: `a.button` / `button.button` are inside `<shopify-account>`'s shadow tree
   * (construct stylesheet + @layer) — no document CSS can target them. If `shadowRoot` is open,
   * inject: `a.button:hover` link text white; `svg.tracking` only for fill; `svg.profile-circle`
   * (or `.profile-cycle` if used) for path stroke. `button.button` (close) white always. If
   * the root is closed, this no-ops.
   */
  function initNotusShopifyAccountButtonStyles() {
    var STYLE_ID = 'notus-shopify-account-button-white';
    var cssText =
      'a.button:hover{color:#fff !important;}' +
      'a.button:hover svg.tracking{color:#fff !important;}' +
      'a.button:hover svg.tracking path,' +
      'a.button:hover svg.tracking [fill=currentColor]{fill:#fff !important;}' +
      'a.button:hover svg.profile-circle path,' +
      'a.button:hover svg.profile-cycle path{stroke:#fff !important;}' +
      'button.button,button.button svg{color:#fff !important;}' +
      'button.button path,button.button svg path,button.button [fill=currentColor]{fill:#fff !important;}' +
      'button.button [stroke=currentColor]{stroke:#fff !important;}';

    function inject(host) {
      if (!host || !host.shadowRoot) return;
      if (host.shadowRoot.getElementById(STYLE_ID)) return;
      var el = document.createElement('style');
      el.id = STYLE_ID;
      el.setAttribute('data-notus', 'shopify-account-buttons');
      el.textContent = '/* notus: account sheet buttons only */\n' + cssText;
      host.shadowRoot.appendChild(el);
    }

    function run() {
      document.querySelectorAll('shopify-account').forEach(function (host) {
        inject(host);
      });
    }

    function scheduleRetries() {
      [0, 50, 200, 600, 1500].forEach(function (ms) {
        setTimeout(run, ms);
      });
    }

    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('shopify-account').then(function () {
      run();
      scheduleRetries();
    });
  }

  /**
   * Offers Sidebar — wires up the #ob-trigger button hardcoded in header.liquid (design-6)
   * to open the drawer rendered by the App Embed block (offers-sidebar.liquid).
   * Close, keyboard, and focus trap are handled by offers-sidebar.js from the App Embed.
   */
  function initOffersTrigger() {
    var triggers = document.querySelectorAll('#ob-trigger, #ob-trigger-mobile, #ob-trigger-compact');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var overlay = document.getElementById('ob-overlay');
        var drawer  = document.getElementById('ob-drawer');
        // Close mobile nav drawer first if open
        var mobileNav = document.getElementById('Drawer-MobileNav');
        if (mobileNav && typeof mobileNav.closeDrawer === 'function') {
          mobileNav.closeDrawer();
        }
        if (!overlay) return;
        overlay.classList.add('is-open');
        document.body.classList.add('ob-locked');
        triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
        setTimeout(function () { if (drawer) drawer.focus(); }, 340);
      });
    });
  }

  /**
   * Price filter debounce — prevents the price range inputs from firing on every keystroke.
   * Waits 600ms after the user stops typing before applying the filter.
   */
  function initPriceFilterDebounce() {
    document.addEventListener('input', function (e) {
      var input = e.target;
      if (!input || !input.closest('[data-filter-type="price_range"]')) return;

      // Clear any existing timer
      if (input._debounceTimer) clearTimeout(input._debounceTimer);

      // Wait 600ms after user stops typing before triggering filter
      input._debounceTimer = setTimeout(function () {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, 600);

      // Prevent the immediate change event firing
      e.stopPropagation();
    }, true);
  }

  /**
   * Hide PDP blocks that render empty markup: custom_liquid promo (CSS fallback above) and
   * variant_picker when there are no option fields and no virtual/personalised variants.
   */
  function notusVariantFlagsHavePersonalisation(root) {
    var scope = root || document;
    var flagsEl = scope.querySelector('[data-notus-variant-flags]');
    if (!flagsEl) return false;
    try {
      var flags = JSON.parse(flagsEl.textContent.trim());
      return Object.keys(flags).some(function (id) {
        var f = flags[id];
        return f && (f.isVirtual === true || f.isPersonalised === true);
      });
    } catch (e) {
      return false;
    }
  }

  function initNotusPdpEmptyBlocks() {
    document.querySelectorAll('.f-product-single__block--custom_liquid').forEach(function (block) {
      if (!block.querySelector('*')) {
        block.classList.add('notus-pdp-block--hidden');
      }
    });

    var variantPickerBlock = document.querySelector('.f-product-single__block--variant_picker');
    if (!variantPickerBlock) return;

    var hasPickerFields = !!variantPickerBlock.querySelector('.variant-picker__field');
    var hasPersonalisation = notusVariantFlagsHavePersonalisation(variantPickerBlock);

    if (!hasPickerFields && !hasPersonalisation) {
      variantPickerBlock.classList.add('notus-pdp-block--hidden');
      return;
    }

    if (!hasPersonalisation) {
      var personalisationRoot = document.getElementById('personalisation-form');
      if (personalisationRoot) {
        personalisationRoot.classList.add('notus-pdp-block--hidden');
      }
    }
  }

  /**
   * Route 2 compact header — swaps which rows are visible, nothing else. Position,
   * transform and pointer-events are left entirely to the theme's own sticky/pin/
   * disappear behaviour on .f-section-header; fighting that caused dead links and a
   * content jump. This only needs its own on/off scroll points because header-scrolled
   * has no buffer and flickers on the smallest scroll jitter right at the edge.
   */
  function initRoute2StickyCompact() {
    var siteHeader = document.querySelector('.site-header:has(.site-header__inner--route2)');
    if (!siteHeader) return;

    // Small and close together on purpose — the swap should start almost as soon as
    // scrolling does, not wait for the full header to scroll out of view. Still two
    // separate points (not one) so 1-2px of jitter can't cross both at once.
    var onThreshold = 16;
    var offThreshold = 4;
    var pinned = false;
    var ticking = false;
    var settleTimer = null;

    function apply(next) {
      pinned = next;
      siteHeader.classList.toggle('route2-pinned', pinned);
    }

    function update() {
      var y = window.scrollY;
      var candidate = pinned;
      if (!pinned && y > onThreshold) {
        candidate = true;
      } else if (pinned && y < offThreshold) {
        candidate = false;
      }

      if (candidate !== pinned) {
        // A couple of pixels of scroll jitter right at the edge can flip this back and
        // forth many times a second — only commit once the new state holds for a beat.
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          settleTimer = null;
          apply(candidate);
        }, 60);
      } else if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
      ticking = false;
    }

    apply(false);
    update();
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
  }

  /**
   * Sticky product info (`.sticky-element`, main-product's "enable sticky info") offsets
   * itself below the pinned header using --f-header-height (assets/theme.css,
   * "body.header-pinned .sticky-element"). The vendor header.js only ever writes that
   * variable from the mega-menu hover handlers — never on scroll — so once the header
   * shrinks on scroll (loses the utility/offers row), the variable is stuck at the tall,
   * unscrolled height. The sticky column then offsets for a header that's no longer that
   * tall: it sits too high, slides under the real (shorter) pinned header, and only
   * corrects itself — with a visible jump — if a mega menu happens to be hovered
   * afterwards. Sitewide (design-6 included, not just route 2). A ResizeObserver keeps
   * the variable live off the header's own real height instead, firing exactly when the
   * box resizes (scroll-triggered class change, mega menu, window resize) so the offset
   * never lags behind what's actually on screen.
   */
  function initStickyHeaderHeightSync() {
    var header = document.querySelector('.f-section-header');
    if (!header || !('ResizeObserver' in window)) return;

    var ticking = false;
    function apply() {
      ticking = false;
      document.documentElement.style.setProperty('--f-header-height', header.getBoundingClientRect().height + 'px');
    }

    var observer = new ResizeObserver(function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    });
    observer.observe(header);
  }

  /**
   * Route 2 utility bar: the "Log in" label sits next to <shopify-account> rather than
   * inside it (a sealed component), so a click on the label alone does nothing on its
   * own. Forward it to the real slotted trigger instead of touching shopify-account.
   */
  function initRoute2AccountLabel() {
    document.querySelectorAll('.site-header__utility-account').forEach(function (wrapper) {
      wrapper.addEventListener('click', function (e) {
        if (e.target.closest('shopify-account')) return;
        var trigger = wrapper.querySelector('[slot="signed-out-avatar"], .account-button__fallback');
        if (trigger) trigger.click();
      });
    });
  }

  /**
   * Route 2 mega menu: hands the open/close entirely to CSS :hover. The vendor JS
   * (assets/header.js, SiteNav) adds a class 160ms after mouseenter and removes the
   * mega-active state instantly on mouseleave — both were fighting the CSS-only fixes
   * already in notus-custom-css.css. A dropdown that opens and closes on hover doesn't
   * need JS at all, so this removes it from the equation rather than racing it further.
   */
  function initRoute2CssOnlyMegaMenu() {
    if (!document.querySelector('.site-header__inner--route2')) return;
    var SiteNavEl = customElements.get('site-nav');
    if (!SiteNavEl || !SiteNavEl.prototype) return;
    SiteNavEl.prototype.onMenuItemEnter = function () {};
    SiteNavEl.prototype.onMenuItemLeave = function () {};
    SiteNavEl.prototype.closeMegaDropdowns = function () {};
  }

  function init() {
    initHeaderInlineSearchDrawer();
    initNotusPdpEmptyBlocks();
    initProductPersonalisation();
    initNotusCartGiftAid();
    initNotusPredictiveSearchSku();
    initNotusShopifyAccountButtonStyles();
    initRoute2CssOnlyMegaMenu();
    initOffersTrigger();
    initPriceFilterDebounce();
    initRoute2AccountLabel();
    initRoute2StickyCompact();
    initStickyHeaderHeightSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();